/**
 * SettingsWorkspace — Iter 191j · Google Workspace + Get-a-Domain.
 *
 * Squarespace-parity Google Workspace setup, plus a curated "buy a domain"
 * section for vendors who don't yet have one. We link out to Ionos, GoDaddy,
 * and Domain.com — the three most globally-available registrars — instead of
 * reselling domains ourselves (keeps our platform fee at 0%).
 *
 * Persistence:
 *   GET  /api/builder/settings                → returns { workspace: {...} }
 *   PUT  /api/builder/settings { workspace: {...} }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Loader2, Save, Mail, Check, ExternalLink,
  ShieldCheck, Sparkles,
} from 'lucide-react';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DEFAULTS = {
  connected: false,
  primary_domain: '',
  admin_email: '',
  seats: 1,
  plan: 'business_starter',      // business_starter | business_standard | business_plus
  mx_verified: false,
};

const PLANS = [
  { id: 'business_starter',   label: 'Business Starter',   price: '$6 / user / month', bullets: ['Custom Gmail (you@yourbrand.com)', '30 GB pooled storage', 'Google Meet · 100 participants'] },
  { id: 'business_standard',  label: 'Business Standard',  price: '$12 / user / month', bullets: ['2 TB pooled storage', 'Meet recording', 'Meet · 150 participants'], badge: 'Popular' },
  { id: 'business_plus',      label: 'Business Plus',      price: '$18 / user / month', bullets: ['5 TB pooled storage', 'eDiscovery & retention', 'Meet · 500 participants'] },
];

/* -------- Curated registrar cards (no affiliate keys — pure referral) -------- */
const REGISTRARS = [
  {
    id: 'ionos',
    label: 'IONOS',
    tagline: 'Best value — .com from $1 first year',
    bullets: ['Bulk pricing worldwide', 'Free WHOIS privacy', '24/7 phone support'],
    url: 'https://www.ionos.com/domains',
    accent: 'from-[#1F63BF] to-[#0C4CA8]',
  },
  {
    id: 'godaddy',
    label: 'GoDaddy',
    tagline: 'Best brand recognition — huge extension catalog',
    bullets: ['Widest TLD selection', 'Auto-renew reminders', 'Marketplace for premium names'],
    url: 'https://www.godaddy.com/domainsearch/find',
    accent: 'from-[#0A8E4C] to-[#046939]',
  },
  {
    id: 'domain-com',
    label: 'Domain.com',
    tagline: 'Simplest checkout — great for first-time buyers',
    bullets: ['Clean pricing (no hidden add-ons)', 'Included email forwarding', 'One-click DNS templates'],
    url: 'https://www.domain.com/',
    accent: 'from-[#A78BFA] to-[#6D28D9]',
  },
];

/* ------------------------------ Primitives ------------------------------ */
function Toggle({ checked, onChange, disabled = false, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled} data-testid={testid}
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-[#A78BFA]' : 'bg-white/15'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </button>
  );
}

function SectionCard({ title, description, children, testid }) {
  return (
    <section className="mt-8 rounded-xl bg-white/[0.03] border border-white/[0.08] p-6" data-testid={testid}>
      <h3 className="text-[15px] font-bold text-white tracking-tight">{title}</h3>
      {description && <p className="mt-1 text-[12.5px] text-[#94A3B8] leading-snug">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/* ---------------------------------- Page ---------------------------------- */
export default function SettingsWorkspace() {
  const [ws, setWs] = useState(DEFAULTS);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const remote = res.data?.workspace;
        if (remote && typeof remote === 'object') {
          setWs({ ...DEFAULTS, ...remote });
        }
      } catch { /* silent */ }
      try {
        const d = await axios.get(`${API}/builder/domains`, { headers: { Authorization: `Bearer ${token}` } });
        setDomains(d.data?.items || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const update = (patch) => { setWs((c) => ({ ...c, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { workspace: ws }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Workspace settings saved');
      setDirty(false);
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const hasDomain = domains.length > 0;
  const verifiedDomains = domains.filter((d) => d.status === 'verified' || d.status === 'live');

  return (
    <div className="min-h-full" data-testid="settings-workspace-page">
      {/* Top rail */}
      <div className="border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BackToSettings />
          <div className="w-px h-4 bg-white/10" />
          <h1 className="text-[13px] font-bold text-white uppercase tracking-[0.18em]">Settings</h1>
        </div>
        <div className="flex items-center gap-3">
          <button className="w-8 h-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.06] rounded-md transition-all" title="Search settings">
            <Search size={14} />
          </button>
          {saving && (<span className="text-[10px] uppercase tracking-[0.14em] text-[#A78BFA] font-bold animate-pulse">Saving…</span>)}
          <button
            type="button" onClick={save} disabled={!dirty || saving}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] transition-all ${
              dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'
            }`}
            data-testid="workspace-save-btn"
          >
            <Save size={12} /> Save
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10">
        <div className="text-[12px] text-[#64748B] mb-3">
          <span className="text-white/70 font-semibold">Domains &amp; Email</span>
          <span className="mx-2">/</span>
          <span className="text-white">Google Workspace</span>
        </div>

        <h2 className="text-[32px] font-bold text-white tracking-tight" style={{ fontFamily: 'Syne, Inter, sans-serif' }}>
          Google Workspace
        </h2>
        <p className="mt-3 text-[14px] text-[#9CA3AF] leading-relaxed max-w-2xl">
          Get professional email (<span className="text-white/80">you@yourbrand.com</span>), Google Drive, Meet,
          Calendar, and more — connected to your custom domain.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-white/50">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : !hasDomain ? (
          // No domain yet → suggest registrars
          <SectionCard
            title="First, get a domain"
            description="Google Workspace requires a custom domain (like yourbrand.com) to send email from. Pick a registrar below to buy one — we don't resell domains, so you'll always pay the wholesale price."
            testid="section-get-domain"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {REGISTRARS.map((r) => (
                <a
                  key={r.id} href={r.url} target="_blank" rel="noreferrer"
                  className="group block rounded-xl overflow-hidden border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-[#A78BFA]/50 transition-all"
                  data-testid={`registrar-card-${r.id}`}
                >
                  <div className={`h-1.5 w-full bg-gradient-to-r ${r.accent}`} />
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-[16px] font-bold text-white tracking-tight">{r.label}</span>
                      <ExternalLink size={14} className="text-white/40 group-hover:text-white transition-colors" />
                    </div>
                    <p className="mt-1 text-[11.5px] text-[#A78BFA] font-semibold">{r.tagline}</p>
                    <ul className="mt-3 space-y-1.5">
                      {r.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-1.5 text-[12px] text-white/70">
                          <Check size={11} className="text-emerald-400 mt-0.5 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/60 group-hover:text-white">
                      Search domains <ExternalLink size={10} />
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
              <Sparkles size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
              <p className="text-[11.5px] text-white/75 leading-snug">
                After you buy a domain, come back to
                {' '}<a href="/website-builder/settings/domains" className="underline text-[#A78BFA] hover:text-white">Settings → Custom Domains</a>
                {' '}and connect it — usually 5 minutes of DNS tweaks. Then finish this Google Workspace setup.
              </p>
            </div>
          </SectionCard>
        ) : (
          // Has a domain → show workspace connection UI
          <>
            <SectionCard
              title="Choose a plan"
              description="You can upgrade or downgrade any time from your Google Admin console."
              testid="section-plans"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PLANS.map((p) => {
                  const active = ws.plan === p.id;
                  return (
                    <button
                      key={p.id} type="button" onClick={() => update({ plan: p.id })}
                      className={`text-left rounded-xl border p-5 transition-all ${
                        active
                          ? 'border-[#A78BFA]/60 bg-[#A78BFA]/10 shadow-[0_0_0_1px_rgba(167,139,250,0.35)]'
                          : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
                      }`}
                      data-testid={`plan-${p.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[14px] font-bold text-white">{p.label}</span>
                        {p.badge && (
                          <span className="inline-flex items-center px-2 py-[3px] rounded-md text-[9px] font-bold uppercase tracking-[0.14em] bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            {p.badge}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-[12px] text-[#A78BFA] font-semibold">{p.price}</p>
                      <ul className="mt-3 space-y-1.5">
                        {p.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-1.5 text-[11.5px] text-white/70">
                            <Check size={10} className="text-emerald-400 mt-0.5 shrink-0" /> {b}
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard
              title="Connect your domain"
              description="Pick which of your verified domains Workspace should send mail from."
              testid="section-connect-domain"
            >
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Primary domain</span>
                <select
                  value={ws.primary_domain}
                  onChange={(e) => update({ primary_domain: e.target.value })}
                  className="mt-2 w-full max-w-md bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                  data-testid="workspace-primary-domain-select"
                >
                  <option value="">Choose a domain…</option>
                  {(verifiedDomains.length ? verifiedDomains : domains).map((d) => (
                    <option key={d.id} value={d.host}>{d.host} {d.status !== 'verified' && d.status !== 'live' ? '(not yet verified)' : ''}</option>
                  ))}
                </select>
              </label>

              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Admin email address</span>
                <input
                  type="email"
                  value={ws.admin_email}
                  onChange={(e) => update({ admin_email: e.target.value.trim() })}
                  placeholder="you@yourbrand.com"
                  className="mt-2 w-full max-w-md bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                  data-testid="workspace-admin-email-input"
                />
              </label>

              <label className="block mt-4">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#64748B] font-bold">Number of seats</span>
                <input
                  type="number" min="1" max="500"
                  value={ws.seats}
                  onChange={(e) => update({ seats: Math.max(1, Number(e.target.value) || 1) })}
                  className="mt-2 w-32 bg-[#0A0A1F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#A78BFA]/60"
                  data-testid="workspace-seats-input"
                />
              </label>

              <div className="mt-5 pt-5 border-t border-white/[0.06] flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-semibold text-white flex items-center gap-2">
                    <ShieldCheck size={13} className="text-emerald-400" /> MX records
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#94A3B8]">
                    Once you finish Google&apos;s setup, we detect the MX records automatically.
                  </p>
                </div>
                <span
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${
                    ws.mx_verified
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25'
                      : 'bg-amber-500/15 text-amber-300 border-amber-400/25'
                  }`}
                  data-testid="workspace-mx-status"
                >
                  {ws.mx_verified ? 'Verified' : 'Pending'}
                </span>
              </div>

              <a
                href="https://workspace.google.com/business/signup/welcome"
                target="_blank" rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#A78BFA] text-[#0E0E23] text-[11px] font-bold uppercase tracking-[0.14em] hover:bg-white transition-all"
                data-testid="workspace-launch-btn"
              >
                <Mail size={12} /> Open Google Workspace setup <ExternalLink size={11} />
              </a>
            </SectionCard>

            <SectionCard title="Already have Workspace elsewhere?" description="Point your existing MX records at this domain and mark it connected below.">
              <div className="flex items-center gap-3">
                <Toggle checked={ws.connected} onChange={(v) => update({ connected: v })} testid="workspace-connected-toggle" />
                <span className="text-[12.5px] text-white/80">
                  Yes, I already manage Google Workspace for this domain
                </span>
              </div>
            </SectionCard>

            <div className="h-16" />
          </>
        )}
      </div>
    </div>
  );
}
